/* eslint semi: 1 */
import { computed, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Schedule } from '@/pinia/stores/schedule/scheduleStore';
import { useScheduleListStore, type ScheduleListStore } from '@/pinia/stores/schedule/scheduleListStore';
import type ApiError from '@/globals/api-error';
import CONST from '@/globals/constants';
import { SCHEDULE_PAGE_SIZE } from '@/pinia/stores/schedule/scheduleQueryStore';
import { useScheduleQuery, type RouteQuery } from '@/composables/schedule/use-schedule-query';
import { LIST_VIEW_CONTEXT_SCHEDULE, ListViewMode, useLocalStorageStore } from '@base-shared/stores/local-storage';

type ScheduleResolution = Schedule['resolution'];

// Schedule item shape as consumed by the grid/table list views, augmented
// with the small subset of display-only fields those views rely on.
export type FormattedSchedule = Schedule & {
  dimensions: string;
  _edit: { uid: string };
};

// Number of items requested per page from the API, also used to derive the
// displayed item range (`minItemsCount`/`maxItemsCount`).
const MAX_DELETE_REFETCH_PAGES = 5;

const KNOWN_RESOLUTION_LABELS = [
  { width: 1280, height: 720, label: '720p' },
  { width: 1920, height: 1080, label: 'HD' },
  { width: 3840, height: 2160, label: '4K' },
];

// Orientations for which the backend's resolution width/height describe the
// screen's un-rotated (logical) size, so they must be swapped to get the
// visual width/height actually seen by the viewer. `rotate180`/`flipVert`/
// `flipHor`/`horizontal` don't change which axis is which, only `rotateLeft`/
// `rotateRight` (a 90°/270° rotation) do.
const SWAPPED_RATIO_ORIENTATIONS: Array<NonNullable<ScheduleResolution>['orientation']> = ['rotateLeft', 'rotateRight'];

/**
 * Formats a schedule item's resolution for display: known sizes get a
 * friendly label (720p/HD/4K), everything else falls back to `width X height`
 * (or `height X width` for non-horizontal orientations).
 * @param {ScheduleResolution} resolution - The schedule item's resolution.
 * @returns {string}
 */
function getResolutionText (resolution?: ScheduleResolution): string {
  const { width, height, orientation } = resolution || {};

  if (!width || !height) {
    return '-';
  }

  const knownLabel = KNOWN_RESOLUTION_LABELS.find(
    (res) => (res.width === width && res.height === height) ||
             (res.width === height && res.height === width)
  )?.label;

  if (knownLabel) {
    return knownLabel;
  }

  return orientation && SWAPPED_RATIO_ORIENTATIONS.includes(orientation) ? `${height} X ${width}` : `${width} X ${height}`;
}

/**
 * Derives the preview image/iframe's requested resolution for a schedule
 * item from the player's actual `resolution` (as returned by the backend,
 * which already accounts for LED-tagged screens), falling back to a default
 * based on the player's orientation (`dir`) when resolution is unavailable.
 * @param {Schedule} schedule
 * @returns {string}
 */
function getScheduleDimensions (schedule: Schedule): string {
  const { width, height } = schedule.resolution || {};
  if (width && height) {
    return `${width}x${height}`;
  }

  return schedule.dir !== 'vertical' ? '1920x1080' : '1080x1920';
}

/**
 * Derives the CSS `aspect-ratio` value and portrait/landscape orientation for
 * a formatted schedule item's preview box, from its `dimensions`
 * (`"WxH"`, itself derived from the player's actual resolution). Covers both
 * regular vertical/horizontal players and LED screens, whose resolution may
 * not match a standard 16:9/9:16 ratio.
 *
 * When the resolution's `orientation` is `rotateLeft`/`rotateRight`, width
 * and height are swapped for this ratio calculation only (the player's
 * actual visual orientation is rotated 90°), without mutating `dimensions`
 * or any other state, which still reflect the backend's raw width/height.
 *
 * `isVertical` is true whenever width <= height, so a square resolution is
 * included: the grid/table list views only use it to pick between fitting
 * the preview to the container's height and centering it horizontally
 * (`isVertical: true`) versus covering the whole container (`isVertical:
 * false`). A square preview stretched to cover a landscape container would
 * be cropped, so it must use the fit-and-center treatment like a portrait
 * one, mirroring the legacy schedule page's centered LED-screen previews.
 * @param {FormattedSchedule} schedule
 * @returns {{ ratio: string, isVertical: boolean }}
 */
function getPreviewAspectRatio (schedule: FormattedSchedule): { ratio: string; isVertical: boolean } {
  const [rawWidth, rawHeight] = schedule.dimensions.split('x').map(Number);

  if (!rawWidth || !rawHeight) {
    return { ratio: '16 / 9', isVertical: false };
  }

  const shouldSwap = !!schedule.resolution?.orientation && SWAPPED_RATIO_ORIENTATIONS.includes(schedule.resolution.orientation);
  const width = shouldSwap ? rawHeight : rawWidth;
  const height = shouldSwap ? rawWidth : rawHeight;

  return { ratio: `${width} / ${height}`, isVertical: width <= height };
}

/**
 * Applies the schedule-v2 list views' minimal per-item display formatting,
 * mirroring only the parts of the legacy schedule page's `filterScheduleList`
 * that v2 actually uses:
 * - `dimensions`: passed to `Utils.filterMediaPreviewUrl`/`trimDataSrc` (via
 *   the list views' injected `$utils`) when building the preview image/iframe
 *   src.
 * - `_edit.uid`: used by the `media-preview-<uid>` class, consistent with how
 *   every other preview iframe in the app is tagged for the global auth
 *   token-refresh broadcast (see `template-target-on-refresh-token` handling
 *   in `plugins/auth.js`).
 *
 * Intentionally does NOT port over the legacy page's background
 * colors/classes, `_isInErrorStatus`/`_playerStatus`/`iFrameStatus` flags,
 * or the dynamic preview card style
 * calculations - none of those are used by the schedule-v2 grid/table list
 * views. Returns a new object; never mutates the store's loaded pages.
 * @param {Schedule} schedule
 * @returns {FormattedSchedule}
 */
function formatScheduleItem (schedule: Schedule): FormattedSchedule {
  return {
    ...schedule,
    dimensions: getScheduleDimensions(schedule),
    _edit: { uid: schedule.scheduleId },
  };
}

// Mirrors SCHEDULE_OFFLINE_STATUSES in the store.
const OFFLINE_STATUSES = ['unconfigured', 'failed', 'disconnected'];

interface ScheduleStatusInfo {
  text: string;
  class: string;
  icon: string;
}

/**
 * Simplified online/offline status display for schedule-v2 list items.
 * Unlike the legacy schedule page, which surfaces more granular player
 * statuses (e.g. "Not Configured"), schedule-v2 only ever distinguishes
 * between online and offline - callers resolve the actual `isOnline` flag
 * and pass it in.
 * @param {boolean} isOnline - Whether the schedule item's player is online.
 * @returns {ScheduleStatusInfo}
 */
function getStatus (isOnline: boolean): ScheduleStatusInfo {
  if (isOnline) {
    return {
      text: 'label.online',
      class: 'bg-green-100 text-green-800',
      icon: 'flowbite:check-outline',
    };
  }

  return {
    text: 'label.offline',
    class: 'bg-red-100 text-red-800',
    icon: 'flowbite:x-outline',
  };
}

/**
 * Derives all display-relevant player state flags from the raw status string
 * stored in `playerStatusById`. Single source of truth for status-driven UI
 * (badge, Setup button, error styling) so grid and table views stay in sync.
 * @param {string | undefined} rawStatus
 */
function getPlayerUiState (rawStatus: string | undefined): {
  isOnline: boolean;
  isUnconfigured: boolean;
  statusInfo: ScheduleStatusInfo;
} {
  const isOnline = rawStatus == null || !OFFLINE_STATUSES.includes(rawStatus);
  const isUnconfigured = rawStatus === 'unconfigured';
  return { isOnline, isUnconfigured, statusInfo: getStatus(isOnline) };
}

/**
 * Composable with shared helpers for rendering schedule list items
 * (used by both the grid and table schedule list views).
 *
 * Also wraps the schedule-v2 store actions/pagination logic so page
 * components don't need to reach into `useScheduleListStore()` directly.
 */
export function useScheduleList () {
  const $apiError = inject('$apiError') as typeof ApiError;
  const store = useScheduleListStore();
  const localStorageStore = useLocalStorageStore();
  const { t } = useI18n();

  // Composables
  const scheduleQuery = useScheduleQuery();

  // View mode
  const listViewMode = computed(() => localStorageStore.listViewModePreferences[LIST_VIEW_CONTEXT_SCHEDULE] ?? 'grid');

  // Loading flags come straight from the store so they stay in sync with the
  // `loadSchedulePage`/`loadNextSchedulePage` requests it manages.
  const isFetchingNextBatch = computed(() => store.isFetchingNextPage);
  const showGridLoader = computed(() => store.isFetchingForList || isFetchingNextBatch.value);

  const currentPage = computed(() => store.currentPage);
  const loadedPageBatches = computed(() => store.schedulePages);
  const pagedScheduleItems = computed(() => {
    const items = loadedPageBatches.value[store.currentPage - 1]?.items || [];
    return items.map((item) => formatScheduleItem(item));
  });

  const totalItemsCount = computed(() => {
    // Keep total hidden while we still have an unknown tail.
    return store.scheduleHasNextPage ? 0 : store.scheduleItems.length;
  });
  const totalPages = computed(() => {
    const loaded = loadedPageBatches.value.length;
    return loaded < 1 ? 1 : loaded;
  });
  const hasPreviousPage = computed(() => store.currentPage > 1);
  const hasLoadedNextPage = computed(() => store.currentPage < loadedPageBatches.value.length);
  const hasNextPage = computed(() => hasLoadedNextPage.value || store.scheduleHasNextPage);

  const minItemsCount = computed(() => {
    if (pagedScheduleItems.value.length < 1) return 0;
    return (store.currentPage - 1) * SCHEDULE_PAGE_SIZE + 1;
  });

  const maxItemsCount = computed(() => {
    if (pagedScheduleItems.value.length < 1) return 0;
    return minItemsCount.value + pagedScheduleItems.value.length - 1;
  });

  const notificationPayload = computed(() => store.notification);

  /**
   * Clamps and navigates to an already-loaded page.
   * @param {number | string} page
   */
  function goToSchedulePage (page: number | string) {
    const nextPage = Number(page);
    if (Number.isNaN(nextPage)) return;

    store.currentPage = Math.min(Math.max(nextPage, 1), totalPages.value);
  }

  /**
   * Handles a paginator page-select event: navigates directly if the page is
   * already loaded, otherwise fetches the next page first (when available)
   * before navigating to it. Navigating always re-clamps against the latest
   * `totalPages`, so a failed/no-op fetch simply keeps the current page.
   * @param {number | string} page
   */
  async function handlePaginatorPageSelect (page: number | string) {
    const targetPage = Number(page);
    if (Number.isNaN(targetPage) || targetPage < 1) return;

    if (targetPage <= loadedPageBatches.value.length) {
      goToSchedulePage(targetPage);
      return;
    }

    if (targetPage === loadedPageBatches.value.length + 1 && store.scheduleHasNextPage) {
      await loadNextSchedulePage();
      goToSchedulePage(targetPage);
    }
  }

  /**
   * Loads the initial page of schedules, replacing any previously loaded
   * pages. Thin wrapper around `useScheduleListStore().loadSchedulePage`.
   * @param {Parameters<useScheduleListStore['loadSchedulePage']>[0]} params
   */
  async function loadSchedulePage () {
    const queryParams = scheduleQuery.getQueryParams();
    return store.loadSchedulePage(queryParams);
  }

  /**
   * Handles a failed schedule page fetch result shared by
   * `loadInitialSchedulePage`/`loadNextSchedulePage`: attempts an automatic
   * re-login-and-retry for unauthorized errors (via `$apiError`), otherwise
   * surfaces a generic error notification. No-ops on success, or when
   * `result` is `null` (e.g. `loadNextSchedulePage`'s no-op case when there
   * is no next page or a fetch is already in flight).
   * @param {Awaited<ReturnType<ScheduleListStore['loadSchedulePage']>> | Awaited<ReturnType<ScheduleListStore['loadNextSchedulePage']>>} result
   * @param {() => void} retry - Re-invoked automatically after a successful re-login.
   */
  function handleSchedulePageError (
    result: Awaited<ReturnType<ScheduleListStore['loadSchedulePage']>> | Awaited<ReturnType<ScheduleListStore['loadNextSchedulePage']>>,
    retry: () => void
  ) {
    if (!result || result.success) {
      return;
    }

    $apiError.handleUnauthorizedError(result.error, { originalRequest: retry });

    if (result.error?.response?.status === CONST.UNAUTHORIZED_CODE) {
      return;
    }

    const errorHeaders = result.error?.response?.headers;
    const requestId = errorHeaders
      ? typeof errorHeaders.get === 'function'
        ? errorHeaders.get('x-amzn-requestid')
        : errorHeaders['x-amzn-requestid'] ?? null
      : null;
    const errorCode = result.error?.response?.status ?? 'N/A';

    store.setNotification({
      notifId: 'schedule-error-notif',
      type: 'danger',
      icon: 'flowbite:info-circle-solid',
      title: t('errors.somethingWentWrong', { errorCode }),
      message: t('errors.somethingWentWrongDescription'),
      ...(requestId && { requestId }),
      closable: true,
    });
  }

  /**
   * Loads the initial page of schedules using the current route query
   * params. Thin wrapper around `loadSchedulePage`.
   */
  async function loadInitialSchedulePage (routeQuery: RouteQuery) {
    scheduleQuery.setScheduleQueryParams(routeQuery);
    const result = await loadSchedulePage();
    handleSchedulePageError(result, () => { loadInitialSchedulePage(routeQuery); });

    return result;
  }

  /**
   * Loads the next page of schedules, appending it to the already loaded
   * pages. Thin wrapper around `useScheduleListStore().loadNextSchedulePage`.
   */
  async function loadNextSchedulePage () {
    const queryParams = scheduleQuery.getQueryParams();
    const result = await store.loadNextSchedulePage(queryParams);
    handleSchedulePageError(result, () => { loadNextSchedulePage(); });

    return result;
  }

  /**
   * After a successful delete, re-loads as many pages as were previously
   * loaded so pagination remains backfilled and the user stays on the closest
   * still-valid page.
   * @param {number} pageCount
   * @param {number} previousPage
   * @param {Parameters<ScheduleListStore['loadSchedulePage']>[0]} query
   * @returns {Promise<{ success: true } | { success: false; error: unknown }>}
   */
  async function refetchPagesAfterDelete (
    pageCount: number,
    previousPage: number
  ): Promise<{ success: true } | { success: false; error: unknown }> {
    const firstPageResult = await loadSchedulePage();
    handleSchedulePageError(firstPageResult, () => { refetchPagesAfterDelete(pageCount, previousPage); });

    if (!firstPageResult.success) {
      return { success: false, error: firstPageResult.error };
    }

    for (let loadedPages = 1; loadedPages < pageCount && store.scheduleHasNextPage; loadedPages++) {
      const nextPageResult = await loadNextSchedulePage();
      if (nextPageResult && !nextPageResult.success) {
        return { success: false, error: nextPageResult.error };
      }
    }

    store.currentPage = Math.min(previousPage, store.schedulePages.length) || 1;

    return { success: true };
  }

  /**
   * Deletes a schedule item and re-fetches enough pages to backfill it.
   * Thin wrapper around `useScheduleListStore().deleteSchedule`.
   * @param {string} scheduleId
   */
  async function deleteSchedule (scheduleId: string) {
    if (!scheduleId) return;

    const pageCount = Math.min(store.schedulePages.length, MAX_DELETE_REFETCH_PAGES);
    const previousPage = store.currentPage;

    const deleteResult = await store.deleteSchedule(scheduleId);
    if (!deleteResult.success) {
      $apiError.handleAPIRequestError(deleteResult.error, {
        originalRequest: () => { deleteSchedule(scheduleId); },
        showDialogNotification: true,
      });
      return deleteResult;
    }

    return refetchPagesAfterDelete(pageCount, previousPage);
  }

  /**
   * Records the live status reported by a schedule row's preview iframe.
   * Thin wrapper around `useScheduleListStore().setSchedulePlayerStatus`.
   * @param {string} scheduleId
   * @param {string} rawStatus
   */
  function setSchedulePlayerStatus (scheduleId: string, rawStatus: string) {
    store.setSchedulePlayerStatus(scheduleId, rawStatus);
  }

  /**
   * Closes the current schedule-v2 list notification, if any. Thin wrapper
   * around `useScheduleListStore().setNotification`.
   */
  function closeNotification () {
    store.setNotification(null);
  }

  /**
   * Sets the schedule-v2 list view mode preference in local storage.
   */
  function setListViewMode (mode: ListViewMode) {
    localStorageStore.setListViewModePreferenceForContext(LIST_VIEW_CONTEXT_SCHEDULE, mode);
  }

  onMounted(() => {
    // Initialize the list view mode from local storage on mount.
    localStorageStore.getListViewModePreferenceForContext(LIST_VIEW_CONTEXT_SCHEDULE);
  });

  return {
    isFetchingNextBatch,
    showGridLoader,
    currentPage,
    loadedPageBatches,
    pagedScheduleItems,
    totalItemsCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    minItemsCount,
    maxItemsCount,
    notificationPayload,
    listViewMode,

    getResolutionText,
    getStatus,
    getPlayerUiState,
    getPreviewAspectRatio,
    goToSchedulePage,
    handlePaginatorPageSelect,
    loadSchedulePage,
    loadNextSchedulePage,
    loadInitialSchedulePage,
    deleteSchedule,
    setSchedulePlayerStatus,
    closeNotification,
    setListViewMode
  };
}
